import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  UseDisclosureProps,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import { logError } from '@/imports/logging/ClientLogger';
import { CameraIcon, Edit2Icon, TrashIcon, UploadIcon } from '@/imports/ui/chakra/feather';

import { ImageUploadModal } from './ImageUploadModal';

type ProfileImageMenuProps = {
  hasProfileImage?: boolean;
  updateProfileImage: (profilePictureId: any) => Promise<any>;
  isLoading?: boolean;
  objectFit?: 'cover' | 'contain';
  iconView?: true
};

export const ProfileImageMenu: React.FC<
  ProfileImageMenuProps & UseDisclosureProps
> = ({
  hasProfileImage,
  updateProfileImage,
  isLoading,
  objectFit,
  iconView=false,
  ...disclosureProps
}) => {
  const modal = useDisclosure(disclosureProps);
  const toast = useToast();

  return (
    <Box
      borderRadius="full"
      boxShadow="base"
      position="absolute"
      bottom={2}
      left="50%"
      transform="translateX(-50%)"
    >
      {hasProfileImage ? (
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Upload Profile Image"
            icon={!iconView? <CameraIcon p="0" boxSize="4" /> : <Edit2Icon />}
            colorScheme="primary"
            borderRadius="full"
            bg={iconView? "primary.500" : ""}
            position={iconView? 'absolute' : "unset"}
            bottom={iconView ? -1 : undefined}
            right={iconView ? -12 : undefined}
            boxSize={iconView ? "24px" : "32px"}
            maxW={iconView ? "24px" : "32px"}
            maxH={iconView ? "24px" : "32px"}
            minH={iconView ? "24px" : "32px"}
            minW={iconView ? "24px" : "32px"}
            />
          <Portal>
            <MenuList>
              <MenuItem icon={<UploadIcon />} onClick={modal.onOpen}>
                Upload New Profile Photo
              </MenuItem>
              <MenuItem
                icon={<TrashIcon />}
                onClick={async () => {
                  try {
                    await updateProfileImage(null);
                    toast({
                      title: 'Successfully removed profile photo',
                    });
                  } catch (error) {
                    logError(error);
                    toast({
                      title: 'Failed to remove profile photo',
                      status: 'error',
                    });
                  }
                }}
              >
                Remove
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      ) : (
        <IconButton
          aria-label="Upload Profile Image"
          icon={<CameraIcon />}
          colorScheme="gray"
          borderRadius="full"
          bg="white"
          onClick={modal.onOpen}
        />
      )}
      <ImageUploadModal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        title="Upload a profile photo"
        subtitle="The image size is 300px by 300px"
        height={300}
        width={300}
        showSizeErrorToast={true}
        onSubmit={async (files) => {
          try {
            await updateProfileImage(files[0]);
            modal.onClose();
            toast({
              title: 'Successfully added profile photo',
            });
          } catch (error) {
            logError(error);
            toast({
              title: 'Failed to add profile photo',
              status: 'error',
            });
          }
        }}
        isLoading={isLoading}
        objectFit={objectFit}
      />
    </Box>
  );
};
